import {
  AllocationUtilizationParams,
  AllocationUtilizationPeriod,
  AllocationUtilizationRow,
  PointsPeriod,
} from '@kudos/http-client'
import { Sorting } from '@shared/search/sorting'
import { boolValue, SearchState } from '@shared/search/state'
import { getCountryByCode } from '@shared/utils/countries'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import { format, isValid, lastDayOfMonth, lastDayOfQuarter, lastDayOfYear, parse } from 'date-fns'

export type AllocationUtilizationSortField =
  | 'name'
  | 'manager'
  | 'country'
  | 'department'
  | 'location'
  | 'pointsAllocated'
  | 'pointsUsed'
  | 'pointsUtilization'

/**
 * Header sort before the user picks a column. Highest utilization first.
 * The name tiebreaker lives on {@link allocationUtilizationDefaultSorting} and
 * stays off the header so only this column shows the arrow.
 */
export const allocationUtilizationInitialSort: Sorting<AllocationUtilizationSortField> = [
  { field: 'pointsUtilization', ascending: false },
]

/** Applied sort: utilization descending, then name, then id for a stable order. */
export const allocationUtilizationDefaultSorting: Sorting<AllocationUtilizationSortField> = [
  ...allocationUtilizationInitialSort,
  { field: 'name', ascending: true },
]

const compareText = (a: string, b: string) => a.localeCompare(b)

/** Blank profile fields sort as empty strings so they group with other blanks. */
export const allocationUtilizationManagerName = (row: AllocationUtilizationRow): string =>
  row.manager ? fullNameFromParts(row.manager.firstName, row.manager.lastName) : ''

export const allocationUtilizationCountry = (row: AllocationUtilizationRow): string => {
  const code = row.locations[0]?.country
  return code ? getCountryByCode(code).name : ''
}

export const allocationUtilizationDepartment = (row: AllocationUtilizationRow): string =>
  row.departments[0]?.name ?? ''

export const allocationUtilizationLocation = (row: AllocationUtilizationRow): string =>
  row.locations[0]?.name ?? ''

const compareName = (a: AllocationUtilizationRow, b: AllocationUtilizationRow) => {
  const byName = compareText(
    fullNameFromParts(a.firstName, a.lastName),
    fullNameFromParts(b.firstName, b.lastName),
  )
  if (byName !== 0) return byName
  return compareText(a.id, b.id)
}

export const allocationUtilizationSortCompare: Record<
  AllocationUtilizationSortField,
  (a: AllocationUtilizationRow, b: AllocationUtilizationRow) => number
> = {
  name: compareName,
  manager: (a, b) =>
    compareText(allocationUtilizationManagerName(a), allocationUtilizationManagerName(b)),
  country: (a, b) => compareText(allocationUtilizationCountry(a), allocationUtilizationCountry(b)),
  department: (a, b) =>
    compareText(allocationUtilizationDepartment(a), allocationUtilizationDepartment(b)),
  location: (a, b) =>
    compareText(allocationUtilizationLocation(a), allocationUtilizationLocation(b)),
  pointsAllocated: (a, b) => a.pointsAllocated - b.pointsAllocated,
  pointsUsed: (a, b) => a.pointsUsed - b.pointsUsed,
  pointsUtilization: (a, b) => a.pointsUtilization - b.pointsUtilization,
}

/**
 * Width of the used-vs-remaining bar, as a 0–100 percentage.
 * Zero allocated points yields an empty bar. Used points past the allocation clamp at 100.
 */
export const thermometerFillPercent = (pointsUsed: number, pointsAllocated: number): number => {
  if (pointsAllocated <= 0) return 0
  const percent = (pointsUsed / pointsAllocated) * 100
  return Math.min(100, Math.max(0, percent))
}

/** Whole points with thousands separators for the user's locale. */
export const formatPoints = (points: number): string => Math.round(points).toLocaleString()

/**
 * Label for the backend utilization rate. That rate is already the ratio of the
 * totals (0–100), so this only rounds it for display.
 */
export const formatPointsUtilization = (pointsUtilization: number): string =>
  `${Math.round(pointsUtilization)}%`

/**
 * Member-row utilization. Keeps a single decimal when the rate isn't whole
 * (the mock shows `12.5%`) and drops it for whole rates (`100%`, `0%`).
 */
export const formatRowPointsUtilization = (pointsUtilization: number): string => {
  const formatted = pointsUtilization.toLocaleString(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })
  return `${formatted}%`
}

const PERIOD_REFERENCE_DATE = new Date(2000, 0, 1)

/**
 * First and last day of a period id (`Y2026M09`, `Y2026Q3`, `Y2026`), matching
 * Syndicate's `dateRangeForPeriod`. Undefined for an id it can't parse.
 */
export const periodDateRange = (period: PointsPeriod): [Date, Date] | undefined => {
  if (period.includes('Q')) {
    const start = parse(period, "'Y'yyyy'Q'Q", PERIOD_REFERENCE_DATE)
    return isValid(start) ? [start, lastDayOfQuarter(start)] : undefined
  }
  if (period.includes('M')) {
    const start = parse(period, "'Y'yyyy'M'MM", PERIOD_REFERENCE_DATE)
    return isValid(start) ? [start, lastDayOfMonth(start)] : undefined
  }
  const start = parse(period, "'Y'yyyy", PERIOD_REFERENCE_DATE)
  return isValid(start) ? [start, lastDayOfYear(start)] : undefined
}

/** Year of a period id, e.g. `2026` for `Y2026M09`. */
export const periodYear = (period: PointsPeriod): string => /^Y(\d{4})/.exec(period)?.[1] ?? ''

/**
 * Range label in the user's locale, e.g. `Sep 1 - Sep 30 2026`, or without the
 * year for items listed under a year header. Falls back to the backend's
 * English `name` if the id can't be parsed.
 */
export const formatPeriodRange = (
  { value, name }: AllocationUtilizationPeriod,
  { withYear }: { withYear: boolean },
): string => {
  const range = periodDateRange(value)
  if (!range) return name
  const [start, end] = range
  return `${format(start, 'MMM d')} - ${format(end, withYear ? 'MMM d yyyy' : 'MMM d')}`
}

/**
 * Export filename for a period id, e.g.
 * `Allocated_points_utilization_Sep_01_2026-Sep_30_2026.xlsx`. Uses the raw id
 * if it can't be parsed.
 */
export const allocationUtilizationExportFilename = (period: PointsPeriod): string => {
  const range = periodDateRange(period)
  const label = range ? range.map(date => format(date, 'MMM_dd_yyyy')).join('-') : period
  return `Allocated_points_utilization_${label}.xlsx`
}

export type AllocationUtilizationPeriodGroup = {
  year: string
  periods: AllocationUtilizationPeriod[]
}

/** Groups periods under their year, keeping the backend's newest-first order. */
export const groupPeriodsByYear = (
  periods: AllocationUtilizationPeriod[],
): AllocationUtilizationPeriodGroup[] =>
  periods.reduce<AllocationUtilizationPeriodGroup[]>((groups, period) => {
    const year = periodYear(period.value)
    const last = groups[groups.length - 1]
    if (last?.year === year) {
      last.periods.push(period)
    } else {
      groups.push({ year, periods: [period] })
    }
    return groups
  }, [])

/**
 * The period to report on: the persisted selection while the backend still
 * lists it, otherwise the most recent period (the current allocation cycle).
 */
export const resolveSelectedPeriod = (
  periods: AllocationUtilizationPeriod[] | undefined,
  selected: PointsPeriod | undefined,
): PointsPeriod | undefined => {
  if (!periods?.length) return undefined
  if (selected && periods.some(period => period.value === selected)) return selected
  return periods[0].value
}

export type AllocationUtilizationFilterField =
  | 'memberIdIn'
  | 'suspended'
  | 'directManagerIdIn'
  | 'teamIdIn'
  | 'hasReports'

export type AllocationUtilizationFilters = Partial<SearchState<AllocationUtilizationFilterField>>

/**
 * Report request for a period and the drawer's filters. Unset filters are
 * omitted. `hasReports` is only ever sent as `true`, which is all the backend
 * accepts.
 */
export const toAllocationUtilizationParams = (
  period: PointsPeriod,
  filters: AllocationUtilizationFilters,
): AllocationUtilizationParams => {
  const params: AllocationUtilizationParams = { period }
  if (filters.memberIdIn?.length) params.memberIdIn = filters.memberIdIn
  if (filters.directManagerIdIn?.length) params.directManagerIdIn = filters.directManagerIdIn
  if (filters.teamIdIn?.length) params.teamIdIn = filters.teamIdIn
  const suspended = boolValue(filters.suspended)
  if (suspended !== undefined) params.suspended = suspended
  if (boolValue(filters.hasReports)) params.hasReports = true
  return params
}
